import { PCODE_IMAGE_MEDIA_SET_ALIAS, PcodeCanvasImageSetItem, PcodeCanvasMediaSet } from '../../typings/pcode';
import { ApiCropData, CropApiImageItem, ImageApiGalleryItem, MediaSets } from '../common/requestsTypings';
import { CreativeType, ResourceType } from '../common/typings';
import {
    CreativeOptionData,
    CreativeOptionsConfig,
    CreativeOptionsData,
    CreativeOptionType,
    Disclaimer,
    DisclaimerCountry
} from '../CreativeOptions/typings';
import { getPreviewResource } from '../FileEditor/helpers';
import { GalleryCommonImageCollectionItem } from '../GalleryPane/typings';

import {
    AddAdaptiveCreativeState,
    AddCommonCreativeState,
    AddCreativeState,
    CommonCreativeItem,
    EditingFileInfo
} from './typings';

export function getAddCreativeFileEditorId(type: CreativeType, id: string, groupName: string, name: string): string {
    return `add-creative-${type}-${id}-${groupName}-${name}`;
}

export function getEnabledGalleriesFromConfig(config: CreativeOptionsConfig): ResourceType[] {
    return Object.keys(config).reduce((acc: ResourceType[], groupName: string) => {
        const groupConfig = config[groupName];

        groupConfig.options.forEach((option: CreativeOptionData) => {
            if (option.type === CreativeOptionType.File && !acc.includes(option.resourceType)) {
                acc.push(option.resourceType);
            }
        });

        return acc;
    }, []);
}

export function addCreativePageLoaded(state: AddCreativeState, isAudio?: boolean): boolean {
    switch (state.type) {
        case CreativeType.VIDEO_ADDITION:
            // для аудио достаточно дождаться получения данных параметров
            if (isAudio) {
                return Boolean(state.creativeOptions);
            }

            return Boolean(state.pcodeVideoPreviewData);

        default:
            // на странице создания common креативов превью подгружать не надо
            return true;
    }
}

export function getDisclaimerItem(
    countries: DisclaimerCountry[],
    countryId?: string | null,
    id?: string
): Disclaimer | undefined {
    if (!countryId) {
        return undefined;
    }

    const country = countries.find(c => String(c.id) === countryId);

    if (!country) {
        return undefined;
    }

    const item = id !== undefined ? country.items.find(i => String(i.id) === id) : country.items[0];

    if (!item) {
        return undefined;
    }

    return item;
}

export function getEditingFileInfo(
    editorId: string,
    mediaSetId: string,
    item: ImageApiGalleryItem | GalleryCommonImageCollectionItem
): EditingFileInfo {
    return {
        editorId,
        fileId: item.id,
        cropSourceUrl: item.cropSourceUrl,
        origSrc: item.url,
        preview: getPreviewResource(item),
        mediaSetId
    };
}

export function getMediaSets(
    item: ImageApiGalleryItem | GalleryCommonImageCollectionItem,
    mediaSet?: PcodeCanvasMediaSet
): PcodeCanvasMediaSet {
    const mediaSetItem = mediaSet ? mediaSet.items[0].items[0] : undefined;
    const alias = mediaSetItem ? mediaSetItem.alias : PCODE_IMAGE_MEDIA_SET_ALIAS;

    return {
        items: [
            {
                type: 'image',
                items: [getMediaSetItem(item, alias)]
            }
        ]
    };
}

export function getMediaSetItem(
    item: ImageApiGalleryItem | GalleryCommonImageCollectionItem,
    alias: string
): PcodeCanvasImageSetItem {
    const { width, height } = item.stillageFileInfo.metadataInfo;

    return {
        fileId: item.id,
        url: item.cropSourceUrl,
        width,
        height,
        alias
    };
}

export function getCroppedMediaSetItem(
    item: CropApiImageItem,
    alias: string,
    fileId: string,
    smartCenter?: ApiCropData
): PcodeCanvasImageSetItem {
    const { width, height } = item.stillageFileInfo.metadataInfo;
    const result: PcodeCanvasImageSetItem = {
        fileId,
        url: item.cropSourceUrl,
        width,
        height,
        alias,
        croppedFileId: item.id
    };

    if (smartCenter) {
        result.smartCenters = [
            {
                x: smartCenter.x,
                y: smartCenter.y,
                w: smartCenter.width,
                h: smartCenter.height
            }
        ];
    }

    return result;
}

export function getMediaSet(mediaSets: MediaSets, id: string): PcodeCanvasImageSetItem {
    return mediaSets[id].items[0].items[0];
}

export const getCroppableOptionsEditorIds = (options: CreativeOptionsData, isAdaptive?: boolean): string[] => {
    return Object.keys(options.optionsConfig).reduce(
        (result, groupName) => {
            options.optionsConfig[groupName].options.forEach(option => {
                if (
                    option.type === CreativeOptionType.File &&
                    option.resourceType === ResourceType.image &&
                    (isAdaptive ? option.isSmartCenterable : option.isCroppable)
                ) {
                    result.push(option.editorId);
                }
            });

            return result;
        },
        [] as string[]
    );
};

export function getItems(state: AddCommonCreativeState | AddAdaptiveCreativeState): CommonCreativeItem[] {
    switch (state.type) {
        case CreativeType.ADAPTIVE:
            if (!state.item) {
                throw 'Item is not defined!';
            }

            return [state.item];

        case CreativeType.COMMON:
        case CreativeType.CPM_GEOPRODUCT:
            if (!state.items) {
                throw 'Items is not defined!';
            }

            return state.items;

        default:
            throw 'Creative type is not supported!';
    }
}

export const getGraphicCreativeState = (
    state: AddCreativeState
): AddCommonCreativeState | AddAdaptiveCreativeState | undefined => {
    switch (state.type) {
        case CreativeType.COMMON:
        case CreativeType.CPM_GEOPRODUCT:
        case CreativeType.ADAPTIVE:
            return state;

        default:
            return undefined;
    }
};
